/**
 * PromoKitModal — artist marketing toolkit for any shareable entity (track,
 * product, clip, release). Smart share link (correct cover on every platform),
 * one-tap social share, downloadable branded cover card, QR code, and captions.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  X, Copy, Check, Share2, Download, QrCode, Loader2, Music, Instagram, Twitter, Facebook, MessageCircle, Sparkles, Film,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PromoKitModal = ({ open, type, id, onClose }) => {
  const [kit, setKit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const load = useCallback(() => {
    if (!type || !id) return;
    setLoading(true);
    fetch(`${API_URL}/api/promo/kit?type=${encodeURIComponent(type)}&id=${encodeURIComponent(id)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setKit(d))
      .catch(() => setKit(null))
      .finally(() => setLoading(false));
  }, [type, id]);

  useEffect(() => { if (open) { setShowQr(false); load(); } }, [open, load]);

  if (!open) return null;

  const shareUrl = kit?.share_url || '';
  const qrUrl = `${API_URL}/api/promo/qr?url=${encodeURIComponent(shareUrl)}`;
  const cardUrl = `${API_URL}/api/promo/card?type=${encodeURIComponent(type)}&id=${encodeURIComponent(id)}`;
  const defaultCaption = kit?.captions?.[0] || '';

  const copy = async (text, label = 'Link copied!') => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
      if (text === shareUrl) { setCopied(true); setTimeout(() => setCopied(false), 1500); }
    } catch { toast.error('Copy failed'); }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: kit?.title || 'NLT Radio', text: defaultCaption, url: shareUrl }); }
      catch (e) { /* cancelled */ }
    } else { copy(`${defaultCaption}`); }
  };

  const openSocial = (platform) => {
    const text = encodeURIComponent(defaultCaption || `Check this out on NLT Radio: ${shareUrl}`);
    const u = encodeURIComponent(shareUrl);
    const map = {
      x: `https://twitter.com/intent/tweet?text=${text}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
      whatsapp: `https://wa.me/?text=${text}`,
    };
    if (map[platform]) window.open(map[platform], '_blank', 'noopener,noreferrer');
  };

  const clipPageUrl = `${API_URL}/api/promo/clip-page?type=${encodeURIComponent(type)}&id=${encodeURIComponent(id)}`;

  // Gesture-safe: open the direct URL (backend serves it as an attachment).
  // Works on iOS Safari, Android, and desktop without popup/download blocking.
  const openAsset = (url) => {
    try {
      const w = window.open(url, '_blank');
      if (!w) window.location.href = url;
    } catch {
      window.location.href = url;
    }
  };

  const downloadCard = () => {
    toast.info('Opening your cover card…');
    openAsset(cardUrl);
  };

  // Open a real clip page in a new tab SYNCHRONOUSLY on tap. This preserves the
  // iOS user-gesture (opening a file URL after an async wait gets blocked and
  // "nothing happens"). The page itself builds the clip, shows progress, then
  // displays the video + a Save/Download button.
  const downloadVideo = () => {
    let w = null;
    try { w = window.open(clipPageUrl, '_blank'); } catch { /* blocked */ }
    if (!w) {
      // popup blocked → navigate current tab to the page instead
      window.location.href = clipPageUrl;
    }
  };

  const genAiCaptions = async () => {
    setAiLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/promo/ai-captions?type=${encodeURIComponent(type)}&id=${encodeURIComponent(id)}`);
      const d = await r.json();
      if (d?.captions?.length) {
        setKit((k) => ({ ...k, captions: d.captions }));
        toast.success(d.ai ? '✨ Fresh AI captions ready!' : 'Captions refreshed');
      } else {
        toast.error('Could not generate captions');
      }
    } catch {
      toast.error('Could not generate captions');
    }
    setAiLoading(false);
  };

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center"
        onClick={onClose}
        data-testid="promo-kit-modal"
      >
        <motion.div
          initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
          className="w-full max-w-md bg-[#0F0D17] rounded-t-3xl sm:rounded-3xl border border-white/10 max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10 sticky top-0 bg-[#0F0D17] z-10">
            <div className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-[#00F0FF]" />
              <h3 className="text-white font-bold">Promote & Share</h3>
            </div>
            <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="promo-close-btn"><X className="w-5 h-5" /></button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="w-7 h-7 text-white/40 animate-spin" /></div>
          ) : !kit ? (
            <div className="p-8 text-center text-white/60" data-testid="promo-error">Couldn't load promo kit. Try again.</div>
          ) : (
            <div className="p-4 space-y-5">
              {/* Preview */}
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 rounded-xl overflow-hidden bg-gradient-to-br from-[#FF007F]/40 to-[#00F0FF]/30 shrink-0">
                  {kit.thumbnail
                    ? <img src={kit.thumbnail} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center"><Music className="w-6 h-6 text-white/70" /></div>}
                </div>
                <div className="min-w-0">
                  <p className="text-white font-bold truncate">{kit.title}</p>
                  <p className="text-white/50 text-sm truncate">{kit.subtitle}</p>
                </div>
              </div>

              {/* Smart link */}
              <div>
                <p className="text-white/70 text-xs font-semibold uppercase tracking-wide mb-1.5">Smart share link</p>
                <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5">
                  <span className="flex-1 text-white/80 text-sm truncate" data-testid="promo-share-url">{shareUrl}</span>
                  <button onClick={() => copy(shareUrl)} className="shrink-0 text-[#00F0FF] hover:text-white transition-colors" data-testid="promo-copy-link">
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-white/40 text-[11px] mt-1.5">Shows the correct cover/thumbnail on every platform.</p>
              </div>

              {/* Social buttons */}
              <div>
                <p className="text-white/70 text-xs font-semibold uppercase tracking-wide mb-2">Share to</p>
                <div className="grid grid-cols-5 gap-2">
                  <SocialBtn testid="promo-share-native" onClick={nativeShare} icon={<Share2 className="w-5 h-5" />} label="Share" />
                  <SocialBtn testid="promo-share-x" onClick={() => openSocial('x')} icon={<Twitter className="w-5 h-5" />} label="X" />
                  <SocialBtn testid="promo-share-facebook" onClick={() => openSocial('facebook')} icon={<Facebook className="w-5 h-5" />} label="FB" />
                  <SocialBtn testid="promo-share-whatsapp" onClick={() => openSocial('whatsapp')} icon={<MessageCircle className="w-5 h-5" />} label="WhatsApp" />
                  <SocialBtn testid="promo-share-instagram" onClick={() => copy(`${defaultCaption}`, 'Caption + link copied — paste into Instagram!')} icon={<Instagram className="w-5 h-5" />} label="IG" />
                </div>
              </div>

              {/* Assets: cover card + QR */}
              <div className="grid grid-cols-2 gap-2">
                <button onClick={downloadCard} className="flex items-center justify-center gap-2 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-semibold transition-colors" data-testid="promo-download-card">
                  <Download className="w-4 h-4" /> Cover card
                </button>
                <button onClick={() => setShowQr((v) => !v)} className="flex items-center justify-center gap-2 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-semibold transition-colors" data-testid="promo-toggle-qr">
                  <QrCode className="w-4 h-4" /> {showQr ? 'Hide QR' : 'QR code'}
                </button>
              </div>
              {type === 'track' && (
                <button onClick={downloadVideo} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white text-sm font-bold transition-opacity hover:opacity-90" data-testid="promo-download-video">
                  <Film className="w-4 h-4" />
                  15s Story video (cover + snippet)
                </button>
              )}
              {showQr && (
                <div className="flex flex-col items-center gap-2 pt-1" data-testid="promo-qr-block">
                  <img src={qrUrl} alt="QR code" className="w-40 h-40 rounded-xl bg-white p-2" />
                  <a href={qrUrl} download="nltradio_qr.png" className="text-[#00F0FF] text-xs font-semibold hover:text-white">Download QR</a>
                </div>
              )}

              {/* Captions */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-white/70 text-xs font-semibold uppercase tracking-wide">Ready-to-post captions</p>
                  <button
                    onClick={genAiCaptions}
                    disabled={aiLoading}
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-[#FF007F]/20 to-[#00F0FF]/20 border border-white/10 text-white text-xs font-semibold hover:from-[#FF007F]/30 hover:to-[#00F0FF]/30 transition-colors disabled:opacity-50"
                    data-testid="promo-ai-captions"
                  >
                    {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 text-[#00F0FF]" />}
                    {aiLoading ? 'Writing…' : 'AI captions'}
                  </button>
                </div>
                <div className="space-y-2">
                  {kit.captions?.map((c, i) => (
                    <button
                      key={i}
                      onClick={() => copy(c, 'Caption copied!')}
                      className="w-full text-left flex items-start gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl px-3 py-2.5 transition-colors"
                      data-testid={`promo-caption-${i}`}
                    >
                      <span className="flex-1 text-white/80 text-sm leading-snug">{c}</span>
                      <Copy className="w-3.5 h-3.5 text-white/40 shrink-0 mt-0.5" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

const SocialBtn = ({ onClick, icon, label, testid }) => (
  <button onClick={onClick} className="flex flex-col items-center gap-1 py-2 rounded-xl bg-white/5 hover:bg-white/15 text-white transition-colors" data-testid={testid}>
    {icon}
    <span className="text-[10px] text-white/60">{label}</span>
  </button>
);

export default PromoKitModal;
